import {
  Box,
  Text,
  ModalContent,
  Tfoot,
  Tr,
  Td,
  ModalHeader,
  Flex,
  Modal,
  ModalOverlay,
  ModalBody,
  Spinner,
  ModalCloseButton,
} from "@chakra-ui/react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { LoanContext } from "providers/loan";
import { useLoanTransactions } from "hooks/loan-transactions/useLoanTransactions";
import { useCustomFormats } from "hooks/useCustomFormat";
import { useStatement } from "hooks/statements/useStatement";
import { jsPDF } from "jspdf";
import { ActionButtons } from "../components/ActionButtons";
import { TableTransactions } from "../components/TableTransactions";
import { Header } from "../components/header";

interface MortgageStatementModalProps {
  onClose: () => void;
  isOpen: boolean;
  lastStatementDate: string;
}

export function MortgageStatementModal({
  onClose,
  isOpen,
  lastStatementDate,
}: MortgageStatementModalProps) {
  const { loan } = useContext(LoanContext);
  const { formatDate, formatCurrency } = useCustomFormats();
  const printRef = useRef<HTMLDivElement>(null);

  const { save: saveStatement, isSaving } = useStatement();

  const [pdf, setPdf] = useState<jsPDF>();

  const { data: statement, isLoading } = useLoanTransactions({
    loan,
    dateRange: {
      start: undefined,
      end: lastStatementDate,
    },
    excludeExitFee: true,
  });

  const firstDate = useMemo(() => {
    return formatDate(statement?.startAt ?? null);
  }, [statement, formatDate]);

  const lastDate = useMemo(() => {
    return formatDate(statement?.endAt ?? null);
  }, [statement, formatDate]);

  const fileName = useMemo(() => {
    return `Mortgage Statement for ${formatDate(lastStatementDate).replace(/\//g, "-")}`;
  }, [formatDate, lastStatementDate]);

  useEffect(() => {
    if (loan?.id && !isLoading && !pdf) {
      const interval = setInterval(() => {
        if (printRef.current) {
          clearInterval(interval);
          saveStatement({
            element: printRef.current,
            name: fileName,
            loanId: loan.id,
          }).then(pdf => {
            setPdf(pdf);
          });
        }
      }, 100);
    }
  }, [loan?.id, isLoading, saveStatement, fileName, pdf]);

  if (!statement) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader display="flex" alignItems="center">
          Mortgage Statement
          <ActionButtons pdf={pdf} isSaving={isSaving} isLoading={isLoading} fileName={fileName} />
        </ModalHeader>
        <ModalBody onWheel={e => e.stopPropagation()} mb={3}>
          {isLoading || !loan ? (
            <Flex justifyContent={"center"} alignItems={"center"} minH={300}>
              <Spinner />
            </Flex>
          ) : (
            <Flex direction="column" gap={6} className="print-area" ref={printRef}>
              <Header name="Mortgage Statement" statement={statement} />

              <Flex direction="column" gap={6}>
                <Flex direction="column" gap={2}>
                  <Text color={"black"} fontStyle="italic" fontWeight="semibold">
                    {`Mortgage statement from ${firstDate} to ${lastDate} (including payments due this period)`}
                  </Text>
                  <Box>
                    <TableTransactions
                      data={statement?.entries ?? []}
                      tfoot={
                        <Tfoot>
                          <Tr fontWeight={"semibold"} fontStyle={"italic"}>
                            <Td border={0} py={8} textAlign={"center"}>
                              {lastDate}
                            </Td>
                            <Td border={0} py={8} colSpan={3}>
                              Final Redemption Amount
                            </Td>
                            <Td border={0} py={8} textAlign={"right"}>
                              {formatCurrency(statement?.remainingBalance ?? 0)}
                            </Td>
                          </Tr>
                        </Tfoot>
                      }
                    />
                    <hr />
                  </Box>
                </Flex>
              </Flex>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
